/**
 * Why the session ended — drives the copy shown by `SessionRevokedGuard` and
 * where the user is redirected on acknowledge.
 *   - `superseded`: signed in from another device (single-session enforcement).
 *   - `expired`: token refresh failed (refresh token expired/revoked). Surfaced
 *     instead of a hard redirect so an active roleplay session isn't cut abruptly.
 *   - `deactivated`: an admin disabled the account; the backend revoked every live
 *     session, so re-authenticating is pointless until it is re-enabled.
 *   - `passwordChanged`: the password was changed; every session was revoked and the
 *     user signs in again with the new password.
 */
export type SessionEndReason =
  | 'superseded'
  | 'expired'
  | 'deactivated'
  | 'deleted'
  | 'linkMismatch'
  | 'passwordChanged';

/**
 * Backend auth error codes that mean the session is over server-side — refreshing would
 * only 401 again. Mirrors `AUTH_ERROR_CODES` in sales-coach-core.
 */
const SESSION_ENDED_CODES: Record<string, SessionEndReason> = {
  SESSION_SUPERSEDED: 'superseded',
  USER_DEACTIVATED: 'deactivated',
  ACCOUNT_DELETED: 'deleted',
  ACCOUNT_LINK_MISMATCH: 'linkMismatch',
  PASSWORD_CHANGED: 'passwordChanged',
};

/** The reason behind a backend auth error code, or null if it isn't one of them. */
export function sessionEndReasonFromCode(code: string | undefined): SessionEndReason | null {
  return (code && SESSION_ENDED_CODES[code]) || null;
}

/**
 * `auth` namespace key holding the copy for each reason (`.title` / `.message` / `.ok`).
 * Shared so the revoked-session modal and the login screen — which can hit the same
 * reason from opposite ends of the flow — can never drift apart.
 */
export const SESSION_END_COPY_KEYS: Record<SessionEndReason, string> = {
  superseded: 'sessionRevoked',
  expired: 'sessionExpired',
  deactivated: 'accountDeactivated',
  deleted: 'accountDeleted',
  linkMismatch: 'accountLinkMismatch',
  passwordChanged: 'passwordChanged',
};

type Listener = (reason: SessionEndReason) => void;

const listeners = new Set<Listener>();
let pending: SessionEndReason | null = null;

export const sessionRevokedNotifier = {
  notify(reason: SessionEndReason = 'superseded'): void {
    pending = reason;
    listeners.forEach((listener) => {
      try {
        listener(reason);
      } catch {
        /* ignore */
      }
    });
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    // Replay if notify() already fired before this subscriber attached.
    if (pending) {
      try {
        listener(pending);
      } catch {
        /* ignore */
      }
    }
    return () => listeners.delete(listener);
  },
};
